import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "redux-store/store";
import { createMultipleKit, errorClean, messageClean } from "redux-store/kit/kit.slice";
import { addKitActivityInProcess, fileSuccessMessage, kitError } from "redux-store/kit/kit.selector";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const MultiKitSection = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch<AppDispatch>();

  const isAdding = useSelector(addKitActivityInProcess);
  const errorMessage = useSelector(kitError);
  const successMsg = useSelector(fileSuccessMessage);

  const validateAndSetFile = (file: File) => {
    if (
      file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.ms-excel" ||
      file.name.endsWith(".xlsx") ||
      file.name.endsWith(".xls")
    ) {
      setSelectedFile(file);
      dispatch(errorClean());
      dispatch(messageClean());
      return true;
    } else {
      alert("Please upload a valid Excel file (.xlsx or .xls)");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return false;
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const handleUpload = () => {
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append("excelFile", selectedFile);
    dispatch(createMultipleKit(formData));
  };

  return (
    <div className="tw-w-[80%] lg:tw-w-[90%] tw-mx-auto tw-py-6 lg:tw-py-12 tw-rounded-xl tw-bg-[#FFFFFF] tw-shadow-sm">
      <div className="tw-w-[80%] tw-mx-auto lg:tw-w-3/4 tw-flex tw-flex-col tw-gap-4 lg:tw-gap-6">
        <div className="tw-flex tw-items-center tw-gap-4">
          <div className="tw-flex tw-items-center tw-justify-center tw-w-12 tw-h-12 lg:tw-w-16 lg:tw-h-16 tw-rounded-full tw-bg-reddishOrange tw-text-white">
            <FileUploadIcon fontSize="large" />
          </div>
          <div>
            <h2 className="tw-font-bold tw-text-lg lg:tw-text-2xl">Bulk Kit Activation</h2>
            <p className="tw-text-sm lg:tw-text-base tw-text-gray-600">
              Upload an Excel file to add multiple test kits at once.
            </p>
          </div>
        </div>

        <div className="tw-flex tw-flex-col lg:tw-flex-row tw-gap-4 tw-items-center lg:tw-items-end">
          <div className="tw-w-full lg:tw-flex-1">
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`tw-border-2 tw-border-dashed tw-rounded-lg tw-p-6 tw-flex tw-flex-col tw-items-center tw-justify-center tw-cursor-pointer tw-transition-all ${
                isDragging
                  ? "tw-border-reddishOrange tw-bg-orange-50 tw-scale-[1.02]"
                  : selectedFile
                    ? "tw-border-green-500 tw-bg-green-50"
                    : "tw-border-gray-300 hover:tw-border-reddishOrange hover:tw-bg-gray-50"
              }`}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="tw-hidden"
                accept=".xlsx, .xls"
              />
              {selectedFile ? (
                <>
                  <CheckCircleOutlineIcon className="tw-text-green-500 tw-mb-2" fontSize="large" />
                  <span className="tw-text-green-700 tw-font-medium">{selectedFile.name}</span>
                  <span className="tw-text-xs tw-text-green-600 mt-1">{(selectedFile.size / 1024).toFixed(2)} KB</span>
                </>
              ) : (
                <>
                  <FileUploadIcon className="tw-text-gray-400 tw-mb-2" fontSize="large" />
                  <span className="tw-text-gray-500">Click to browse or drag and drop</span>
                  <span className="tw-text-xs tw-text-gray-400 mt-1">Excel files only (.xlsx, .xls)</span>
                </>
              )}
            </div>
          </div>

          <button
            onClick={handleUpload}
            disabled={!selectedFile || isAdding}
            className={`tw-w-full lg:tw-w-40 tw-py-3 tw-rounded-lg tw-font-bold tw-text-white tw-transition-all ${
              !selectedFile || isAdding
                ? "tw-bg-gray-300 tw-cursor-not-allowed"
                : "tw-bg-reddishOrange hover:tw-bg-opacity-90 active:tw-scale-95 shadow-md"
            }`}
          >
            {isAdding ? (
              <span className="tw-flex tw-items-center tw-justify-center">
                <span className="tw-animate-spin tw-mr-2">⌛</span> Adding...
              </span>
            ) : (
              "Add Kits"
            )}
          </button>

          <a
            href="/sample_kits.xlsx"
            download
            className="tw-flex tw-items-center tw-gap-2 tw-text-reddishOrange tw-font-semibold tw-text-sm hover:tw-underline lg:tw-mb-4"
          >
            <SaveAltIcon fontSize="small" />
            Download Sample Format
          </a>
        </div>

        {errorMessage?.message && (
          <div className="tw-mt-2 tw-p-3 tw-bg-red-50 tw-text-red-600 tw-rounded-md tw-text-sm tw-font-medium">
            Error: {errorMessage.message}
          </div>
        )}

        {successMsg && (
          <div className="tw-mt-2 tw-p-3 tw-bg-green-50 tw-text-green-700 tw-rounded-md tw-text-sm tw-font-medium">
            Success: {successMsg}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiKitSection;
